/* ------------------------------------------------------------------
   1-D divide-and-conquer Peak Finder for seasonal flu curves
   ------------------------------------------------------------------ */

interface Point {
  x: string
  y: number
}

interface Options {
  smoothWindow?: number // centred MA length (odd)     | 7
  minDaysBetween?: number // gap between seasons (days)  | 45
  retraceRatio?: number // fall / rise ≥ ratio         | 0.5
  dataInterval?: 'daily' | 'weekly' // data interval type | 'daily'
}

function findPeaks(
  data: readonly Point[],
  opt: Options = {}
): Array<Point & { index: number }> {
  const dataInterval = opt.dataInterval ?? 'daily'
  const isWeekly = dataInterval === 'weekly'

  // Adjust defaults based on data interval
  const W = opt.smoothWindow ?? (isWeekly ? 3 : 7) // 3 weeks vs 7 days (better for weekly data)
  const GAP = opt.minDaysBetween ?? (isWeekly ? 45 : 45) // Keep same day gap
  const RETR = opt.retraceRatio ?? 0.5
  if (data.length < 3) return []

  /* ---------- 1. smooth once (centred moving average) ----------- */
  const half = Math.floor(W / 2)
  const smooth = data.map((_, i) => {
    let s = 0,
      n = 0
    for (let k = -half; k <= half; k++) {
      const j = i + k
      if (j >= 0 && j < data.length) {
        s += data[j].y
        n++
      }
    }
    return s / n
  })

  // For weekly data, we might want to adjust the smoothing to reduce peak shift
  // We can try using a different approach for peak detection that works on the original data
  // but uses the smoothed data for the divide-and-conquer algorithm
  const dataForPeakDetection = isWeekly ? data.map((d) => d.y) : smooth

  /* ---------- 2. helpers --------------------------------------- */
  function days(a: string, b: string) {
    return Math.abs((Date.parse(b) - Date.parse(a)) / 86_400_000)
  }

  // For weekly data, we need to check index distance differently
  function getMinIndexGap() {
    // For weekly data, we want the same temporal gap (in weeks)
    // If GAP is 45 days, that's about 6.4 weeks, so we'll use 7 weeks
    return isWeekly ? Math.ceil(GAP / 7) : GAP
  }

  function retraceOK(pk: number, lt: number, rt: number) {
    // For weekly data, use original data for more accurate retrace calculation
    if (isWeekly) {
      const rise = dataForPeakDetection[pk] - dataForPeakDetection[lt]
      const retrace = dataForPeakDetection[pk] - dataForPeakDetection[rt]
      return rise > 0 && retrace >= RETR * rise
    }

    const rise = smooth[pk] - smooth[lt]
    const retrace = smooth[pk] - smooth[rt]
    return rise > 0 && retrace >= RETR * rise
  }

  /* ---------- 3. 1-D peak-finder (returns one peak) ------------- */
  function peak1D(lo: number, hi: number): number | null {
    if (hi - lo < 2) return null // need ≥3 points
    const mid = Math.floor((lo + hi) / 2)

    // If left neighbor is strictly greater, search left
    if (smooth[mid - 1] > smooth[mid]) return peak1D(lo, mid - 1)
    // If right neighbor is strictly greater, search right
    if (smooth[mid + 1] > smooth[mid]) return peak1D(mid + 1, hi)

    // If neither neighbor is strictly greater, mid is a candidate peak
    // For weekly data, we need to verify this against the original data
    if (isWeekly) {
      // Find the actual peak in the original data around this point
      let actualPeak = mid
      let maxValue = dataForPeakDetection[mid]

      // Check a small window around the smoothed peak
      const window = Math.min(1, half)
      for (
        let i = Math.max(lo, mid - window);
        i <= Math.min(hi, mid + window);
        i++
      ) {
        if (dataForPeakDetection[i] > maxValue) {
          maxValue = dataForPeakDetection[i]
          actualPeak = i
        }
      }

      return actualPeak
    }

    // For daily data or when no adjustment is needed, use the smoothed peak
    // Find the extent of the plateau and select the rightmost point
    let peakEnd = mid
    while (peakEnd + 1 <= hi && smooth[peakEnd + 1] === smooth[mid]) {
      peakEnd++
    }
    return peakEnd
  }

  /* ---------- 4. main recursive collector ---------------------- */
  const peaks: Array<Point & { index: number }> = []

  function collect(lo: number, hi: number) {
    const pk = peak1D(lo, hi)
    if (pk === null) return

    /* ---- Must satisfy 50 % retrace rule ----------------------- */
    // find nearest trough left / right of pk inside current window
    let lt = pk,
      rt = pk
    if (isWeekly) {
      // For weekly data, use original data for trough finding
      while (
        lt - 1 >= lo &&
        dataForPeakDetection[lt - 1] <= dataForPeakDetection[lt]
      )
        lt--
      while (
        rt + 1 <= hi &&
        dataForPeakDetection[rt + 1] <= dataForPeakDetection[rt]
      )
        rt++
    } else {
      // For daily data, use smoothed data
      while (lt - 1 >= lo && smooth[lt - 1] <= smooth[lt]) lt--
      while (rt + 1 <= hi && smooth[rt + 1] <= smooth[rt]) rt++
    }

    if (lt > lo && rt < hi && retraceOK(pk, lt, rt)) {
      peaks.push({ ...data[pk], index: pk })

      // recurse on left & right segments separated by season gap
      const gapIdx = (idx: number, dir: -1 | 1) => {
        let j = idx
        if (isWeekly) {
          // For weekly data, use index distance instead of date calculation
          const minGap = getMinIndexGap()
          while (j >= lo && j <= hi && Math.abs(j - pk) < minGap) j += dir
        } else {
          // For daily data, use the original date-based calculation
          while (j >= lo && j <= hi && days(data[pk].x, data[j].x) < GAP)
            j += dir
        }
        return j
      }
      const leftEnd = gapIdx(pk - 1, -1)
      const rightBeg = gapIdx(pk + 1, 1)
      if (leftEnd - lo >= 2) collect(lo, leftEnd)
      if (hi - rightBeg >= 2) collect(rightBeg, hi)
    }
    // If retrace rule fails, still inspect both halves
    else {
      const mid = pk
      if (mid - lo >= 2) collect(lo, mid - 1)
      if (hi - mid >= 2) collect(mid + 1, hi)
    }
  }

  collect(0, data.length - 1)

  /* ---------- 5. global-max safety net ------------------------- */
  const idxMax = isWeekly
    ? dataForPeakDetection.reduce(
        (m, v, i) => (v > dataForPeakDetection[m] ? i : m),
        0
      )
    : smooth.reduce((m, v, i) => (v > smooth[m] ? i : m), 0)
  if (!peaks.some((p) => p.index === idxMax)) {
    peaks.push({ ...data[idxMax], index: idxMax })
  }

  return peaks.sort((a, b) => a.index - b.index)
}

export default findPeaks
